<template>
  <div class="tag-search">
    <ui-input
      v-if="searchBy === 'text-and'"
      v-model="modelText"
      :class="[
        'tag-search__input', 
        'tag-search__input--text'
      ]"
      prepend-inner-icon="fas fa-search"
      :placeholder="t('Search')"
      :hide-details="1"
      @keyup="searchByText"
      @focus="handleInputFocus"
      @blur="handleInputBlur"
    >
      <template #append>
        <tag-search-append
          :has-clear="isShowClear"
          :has-marks="hasMarks"
          :hide-tags="hideTags"
          :selected="switchSearchMode"
          @update:selected="switchSearchMode = $event"
          @clear="handleClear"
        ></tag-search-append>
      </template>
    </ui-input>

    <tag-search-select
      v-if="isTags"
      :hideTags="hideTags"
      v-model="modelTags"
      :hasMarks="hasMarks"
      :searchTags="searchTags"
      :hasClear="isShowClear"
      :switch-search-mode="switchSearchMode"
      @update:switch-search-mode="switchSearchMode = $event"
      @handleInputBlur="handleInputBlur"
      @handleInputFocus="handleInputFocus"
      @handleTagsChange="handleTagsChange"
      @handleClear="handleClear"
    />

    <text-or-search-select
      v-if="searchBy === 'text-or'"
      :hideTags="hideTags"
      v-model="modelText"
      :hasMarks="hasMarks"
      :hasClear="isShowClear"
      :switch-search-mode="switchSearchMode"
      @update:switch-search-mode="switchSearchMode = $event"
      @handleInputBlur="handleInputBlur"
      @handleInputFocus="handleInputFocus"
      @handleTagsChange="handleTagsChange"
      @handleClear="handleClear"
    />

    <ui-select
      key="marks"
      v-if="hasMarks && isMarks"
      v-model="modelMarks"
      :class="[
        'tag-search__input',
        'tag-search__input--combobox'
      ]"
      prepend-inner-icon="fas fa-search"
      :items="iconList"
      item-text="text"
      item-value="value"
      :return-object="true"
      chips
      multiple
      :combobox="true"
      button-style="text"
      :placeholder="t('Marks')"
      :hide-details="1"
      @blur="handleInputBlur"
      @focus="handleInputFocus"
    >
      <template #selection="{ item }">
        <ui-label
          class="nomarks-text"
          v-if="markItem(item).value === -1"
          :color="markItem(item).color"
        >{{ markItem(item).label }}</ui-label>
        <ui-icon
          v-else
          class="ml-4 ml-first-0"
          size="12"
          :color="`${markItem(item).color} !important`"
          :icon="markItem(item).icon"
        ></ui-icon>
      </template>

      <template #item="{ item, props: listItemProps }">
        <v-list-item
          v-bind="markListItemBind(listItemProps)"
          :link="false"
          :title="null"
          @click.stop="onMarkListItemClick(listItemProps, $event)"
        >
          <ui-label :customStyle="{ textColor: item.raw.color }" size="12">{{ item.raw.label }}</ui-label>
          <ui-icon
            v-if="item.raw.value !== -1"
            size="12"
            :color="item.raw.color"
            :icon="item.raw.icon"
          ></ui-icon>
        </v-list-item>
      </template>

      <template #append>
        <tag-search-append
          :has-clear="isShowClear"
          :has-marks="hasMarks"
          :hide-tags="hideTags"
          :selected="switchSearchMode"
          @update:selected="switchSearchMode = $event"
          @clear="handleClear"
        ></tag-search-append>
      </template>
    </ui-select>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import query from '@/components/query/query-constructor';
import { MarksFilterModeEnum, TagsLogicalRelationEnum, TextLogicalRelationEnum } from '@/services/models/Enums';
import { useI18n } from '@/composables/useI18n';
import { useColors } from '@/composables/useColors';
import { useUrl } from '@/composables/useUrl';
import { useTableQuery } from '@/composables/useTableQuery';
import { usePickerPushUrl } from '@/composables/usePickerPushUrl';
import { $emitter } from '@/services/emitter';
import TagSearchAppend from '@/views/reports/pickers/tag-search-append.vue';
import TagSearchSelect from '@/views/reports/pickers/tag-search-select.vue';
import TextOrSearchSelect from '@/views/reports/pickers/text-or-search-select.vue';

const { t } = useI18n();
const $colors = useColors();
const route = useRoute();
const { computedType, isMainTab, isReport } = useUrl();
const tableQueryApi = useTableQuery();
const { pushReportUrl } = usePickerPushUrl();

const searchTags = ref('');

type MarkItem = {
  label?: string;
  text: string;
  color: string;
  value: number;
  icon?: string;
};

const iconList = computed((): MarkItem[] => [
  { label: t('No mark'), text: 'no-mark', color: $colors.black, value: -1 },
  { icon: 'fas fa-ban', text: 'ban', color: $colors.red, value: 0 },
  { icon: 'fas fa-minus', text: 'minus', color: $colors.red, value: 1 },
  { icon: 'fas fa-plus', text: 'plas', color: $colors.green, value: 2 },
  { icon: 'fas fa-check', text: 'check', color: $colors.green, value: 3 },
  { icon: 'fas fa-pause', text: 'pause', color: $colors.graphite, value: 4 },
  { icon: 'fas fa-heart', text: 'heart', color: $colors.red, value: 5 },
  { icon: 'fas fa-star', text: 'star', color: $colors.tangerine, value: 6 },
  { icon: 'fas fa-trash', text: 'heart', color: $colors.graphite, value: 7 },
]);

const markItem = (item: { raw?: MarkItem } & Partial<MarkItem>): MarkItem =>
  (item?.raw || item) as MarkItem;

const markListItemBind = (itemProps: Record<string, unknown> = {}) => {
  const { title: _title, subtitle: _subtitle, onClick: _onClick, ...rest } = itemProps;
  return rest;
};

const onMarkListItemClick = (itemProps: Record<string, unknown>, event: MouseEvent) => {
  if (itemProps.disabled) return;
  const handler = itemProps.onClick;
  if (typeof handler === 'function') {
    (handler as (event: MouseEvent) => void)(event);
    return;
  }
  if (Array.isArray(handler)) {
    for (const fn of handler) {
      if (typeof fn === 'function') {
        (fn as (event: MouseEvent) => void)(event);
      }
    }
  }
};

const hasMarks = computed(
  () => !!computedType.value.includes('custom') || isMainTab.value,
);

const searchBy = computed(() => tableQueryApi.tableQueryTemporarySearch.value.searchBy as string | undefined);

const isTags = computed(() => searchBy.value?.startsWith('tags'));

const isMarks = computed(
  () => searchBy.value === 'marks' || !!searchBy.value?.startsWith('marks-'),
);

const hideTags = computed(() => {
  const { name } = route;
  const { type } = route.params;

  if (['app.postbacks', 'app.conversions', 'app.errors'].includes(name as string)) {
    return true;
  }
  return (
    name === 'app.report' &&
    [
      'countries', 'languages', 'cities', 'states', 'isps', 'mobile-carriers',
      'connection-types', 'screen-resolutions', 'device-models', 'device-types',
      'device-vendors', 'os-versions', 'browsers', 'browser-versions', 'useragents', 'oses',
    ].includes(type as string)
  );
});

const switchSearchMode = computed({
  get: () => {
    const search = tableQueryApi.tableQueryTemporarySearch.value;
    const currentSearchBy = (search.searchBy as string) || 'text-and';
    if (currentSearchBy === 'marks') {
      return search.marksFilterMode === MarksFilterModeEnum.exclude
        ? 'marks-exclude'
        : 'marks-include';
    }
    return currentSearchBy;
  },
  set: (value: string) => {
    const current = tableQueryApi.tableQueryTemporarySearch.value;
    tableQueryApi.patchTableQuerySearchTemporary({
      searchBy: value,
      ...(value.startsWith('text')
        ? {
            textValue: value === current.searchBy
              ? (current.textValue || '')
              : (value.includes('and') ? '' : []),
            textLogicalRelation: value.includes('and')
              ? TextLogicalRelationEnum.and
              : TextLogicalRelationEnum.or,
          }
        : {}),
      ...(value.startsWith('tags')
        ? {
            tagsValue: current.tagsValue || [],
            tagsLogicalRelation: value.includes('and')
              ? TagsLogicalRelationEnum.and
              : TagsLogicalRelationEnum.or,
          }
        : {}),
      ...(value === 'marks' || value.startsWith('marks-')
        ? {
            marksValue: current.marksValue || [],
            marksFilterMode: value.includes('exclude')
              ? MarksFilterModeEnum.exclude
              : MarksFilterModeEnum.include,
          }
        : {}),
    });
  },
});

const modelText = computed({
  get: () => {
    const value = tableQueryApi.tableQueryTemporarySearch.value.textValue;
    if (searchBy.value === 'text-or') {
      if (Array.isArray(value)) {
        return value.filter((item) => !!String(item).trim());
      }
      const trimmed = String(value || '').trim();
      return trimmed ? [trimmed] : [];
    }
    return value;
  },
  set: (value: string | string[]) => {
    const currentSearchBy = tableQueryApi.tableQueryTemporarySearch.value.searchBy as string;
    tableQueryApi.patchTableQuerySearchTemporary({
      searchBy: currentSearchBy,
      textValue: currentSearchBy.includes('and')
        ? (value as string || '').trim()
        : (value as string[]).map(item => item.trim()).filter(item => !!item),
      textLogicalRelation: currentSearchBy.includes('and')
        ? TagsLogicalRelationEnum.and
        : TagsLogicalRelationEnum.or,
    });
  },
});

const modelMarks = computed({
  get: () => {
    const iconListObject = Object.values(iconList.value);
    return (tableQueryApi.tableQueryTemporarySearch.value.marksValue as number[] | undefined)?.map(
      item => iconListObject[item + 1],
    );
  },
  set: (value: Array<MarkItem | number>) => {
    const current = tableQueryApi.tableQueryTemporarySearch.value;
    const marksFilterMode = String(current.searchBy || '').includes('exclude')
      || current.marksFilterMode === MarksFilterModeEnum.exclude
      ? MarksFilterModeEnum.exclude
      : MarksFilterModeEnum.include;
    const marksValue = Array.isArray(value)
      ? value
          .map((item) => (typeof item === 'object' && item != null ? item.value : item))
          .filter((item) => item !== undefined && item !== null)
      : (current.marksValue || []);
    tableQueryApi.patchTableQuerySearchTemporary({
      searchBy: `marks-${marksFilterMode}`,
      marksFilterMode,
      marksValue,
    });
  },
});

const modelTags = computed({
  get: () => tableQueryApi.tableQueryTemporarySearch.value.tagsValue,
  set: (value: string[]) => {
    const currentSearchBy = tableQueryApi.tableQueryTemporarySearch.value.searchBy as string;
    const trimValues = value.map(item => item.trim()).filter(item => !!item);
    tableQueryApi.patchTableQuerySearchTemporary({
      searchBy: currentSearchBy,
      tagsValue: trimValues,
      ...(currentSearchBy.startsWith('tags')
        ? {
            tagsLogicalRelation: searchBy.value?.includes('and')
              ? TagsLogicalRelationEnum.and
              : TagsLogicalRelationEnum.or,
          }
        : {}),
    });
  },
});

const isShowClear = computed(() => {
  const currentSearchBy = tableQueryApi.tableQueryTemporarySearch.value.searchBy as string | undefined;
  if (currentSearchBy?.startsWith('tags')) {
    return !!(tableQueryApi.tableQueryTemporarySearch.value.tagsValue as unknown[])?.length;
  }
  if (currentSearchBy === 'marks' || currentSearchBy?.startsWith('marks-')) {
    return !!(tableQueryApi.tableQueryTemporarySearch.value.marksValue as unknown[])?.length;
  }
  if (currentSearchBy === 'text-and' || currentSearchBy === 'text-or') {
    return !!(tableQueryApi.tableQueryTemporarySearch.value.textValue as unknown[])?.length;
  }
  return false;
});

const handleInputFocus = () => {};

const handleInputBlur = () => {};

const handleClear = () => {
  const currentSearchBy = searchBy.value;
  let name: string;
  let value: string | unknown[];

  if (currentSearchBy?.startsWith('text')) {
    name = 'textValue';
    value = currentSearchBy.includes('or') ? [] : '';
  } else if (currentSearchBy === 'marks' || currentSearchBy?.startsWith('marks-')) {
    const current = tableQueryApi.tableQueryTemporarySearch.value;
    const marksFilterMode = currentSearchBy?.includes('exclude')
      || current.marksFilterMode === MarksFilterModeEnum.exclude
      ? MarksFilterModeEnum.exclude
      : MarksFilterModeEnum.include;
    tableQueryApi.patchTableQuerySearchTemporary({
      searchBy: `marks-${marksFilterMode}`,
      marksFilterMode,
      marksValue: [],
    });
    return;
  } else if (currentSearchBy?.startsWith('tags')) {
    name = 'tagsValue';
    value = [];
  } else {
    return;
  }

  tableQueryApi.patchTableQuerySearchTemporary({
    searchBy: currentSearchBy,
    [name]: value,
  });
};

const handleTagsChange = () => {
  searchTags.value = '';
};

const applyDataFromUrl = () => {
  const {
    search,
    marks,
    tags,
    tagsLogicalRelation,
    textLogicalRelation = 'and',
    searchBy: urlSearchBy = 'text',
    marksFilterMode,
  } = query.decode(route.params.data as string);

  switch (urlSearchBy) {
    case 'text':
      tableQueryApi.patchTableQuerySearchTemporary({
        searchBy: `text-${textLogicalRelation}`,
        textLogicalRelation: textLogicalRelation || 'and',
        textValue: search || '',
      });
      break;
    case 'marks': {
      const mode = marksFilterMode === MarksFilterModeEnum.exclude
        ? MarksFilterModeEnum.exclude
        : MarksFilterModeEnum.include;
      tableQueryApi.patchTableQuerySearchTemporary({
        searchBy: `marks-${mode}`,
        marksFilterMode: mode,
        marksValue: marks || [],
      });
      break;
    }
    case 'tags':
      tableQueryApi.patchTableQuerySearchTemporary({
        searchBy: `tags-${tagsLogicalRelation}`,
        tagsLogicalRelation,
        tagsValue: tags || [],
      });
      break;
  }
};

const onBusApplyFilters = () => {
  tableQueryApi.applyTableQuerySearch();
  pushReportUrl();
};

const onBusReportTabAdded = () => {
  applyDataFromUrl();
  tableQueryApi.applyTableQuerySearch();
};

const isAppliedSearchObject = () => {
  const appliedSearch = tableQueryApi.tableQueryForCurrentTab.value.search;
  return !!appliedSearch && typeof appliedSearch === 'object' && !Array.isArray(appliedSearch);
};

const onBusReportTabUpdated = () => {
  if (!searchBy.value || !isAppliedSearchObject()) {
    if (!searchBy.value) {
      tableQueryApi.patchTableQuerySearchTemporary({
        searchBy: 'text-and',
        textLogicalRelation: 'and',
        textValue: '',
      });
    }
    applyDataFromUrl();
    tableQueryApi.applyTableQuerySearch();
  }
};

const searchByText = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    $emitter.emit('click-apply-filters');
  }
};

watch(
  () => tableQueryApi.tableQueryForCurrentTab.value.groupBy,
  () => {
    if (
      (isMarks.value && !hasMarks.value) ||
      (searchBy.value?.startsWith('tags') && hideTags.value) ||
      (searchBy.value === 'text-or' && hideTags.value)
    ) {
      switchSearchMode.value = 'text-and';
      $emitter.emit('click-apply-filters');
    }
  },
  { immediate: true },
);

watch(
  () => tableQueryApi.tableQueryForCurrentTab.value.groupBy,
  () => {
    if (!searchBy.value) {
      tableQueryApi.patchTableQuerySearchTemporary({
        searchBy: 'text-and',
        textLogicalRelation: 'and',
        textValue: '',
      });
      tableQueryApi.applyTableQuerySearch();
      pushReportUrl();
    }
  },
);

onMounted(() => {
  $emitter.on('apply-filters', onBusApplyFilters);
  $emitter.on('report-tab-added', onBusReportTabAdded);
  $emitter.on('report-tab-updated', onBusReportTabUpdated);

  if (!isReport.value) {
    applyDataFromUrl();
    tableQueryApi.applyTableQuerySearch();
  }
});

onBeforeUnmount(() => {
  $emitter.off('apply-filters', onBusApplyFilters);
  $emitter.off('report-tab-added', onBusReportTabAdded);
  $emitter.off('report-tab-updated', onBusReportTabUpdated);
});
</script>

<style lang="scss">
.nomarks-text{
  line-height: 10px;
  margin-left: 4px;
  font-size: 8px;
}
</style>
<style lang="scss">
  .tag-search {
    margin: 6px 8px 0 8px;

    .ui-input.ui-input--controls-hide-details {
      height: 32px;
    }

    .ui-input.ui-input--text .v-field {
      --v-input-control-height: 32px;
      --v-field-padding-top: 0;
      --v-field-padding-bottom: 0;
      --v-field-input-padding-top: 0;
      --v-field-input-padding-bottom: 0;
      height: 32px;
      min-height: 32px;
    }

    input,
    .v-field__input,
    .v-field__input input {
      font-size: 12px;
      line-height: 30px;
    }

    input::placeholder,
    .v-field__input::placeholder,
    .v-field__input input::placeholder {
      font-size: 12px;
      line-height: 30px;
    }

    .v-input__prepend-inner,
    .v-field__prepend-inner {
      position: absolute;
      left: 8px;
      margin-top: 2px;
      padding: 0 4px 0 0;
      height: 24px;
      align-items: center;
    }

    .v-field__prepend-inner .v-icon {
      font-size: 13px !important;
      line-height: 13px;
    }

    .v-input__details {
      display: none;
    }

    .v-field {
      --v-field-padding-top: 0;
      --v-field-padding-bottom: 0;
      --v-field-input-padding-top: 0;
      --v-field-input-padding-bottom: 0;
    }
    
    &__input {
      width: 100%;
      @at-root #{selector-append(&, & + '--text')} {
        .v-input__append-inner,
        .v-field__append-inner {
          align-items: center;
          height: 100%;
          margin: 0;
        }

        .v-text-field__slot > input,
        .ui-input.ui-input--text.v-text-field input,
        .v-field__input {
          padding-left: 24px;
          padding-right: 68px;
        }
      }

      &--combobox {
        .v-field__input {
          padding-left: 24px;
          padding-right: 68px;
          min-height: 30px;
          height: 30px;
          font-size: 12px;
          line-height: 30px;
          flex-wrap: nowrap;
          overflow: hidden;
          align-items: center;
        }

        input {
          font-size: 12px;
          line-height: 30px;
          height: 30px;
          max-height: 30px;
        }

        .v-input__append-inner,
        .v-field__append-inner {
          position: absolute;
          right: 0;
          margin: 0;
          height: 100%;
          align-items: center;
          padding-left: 4px;
        }

        .v-select__slot {
          display: flex;
          justify-content: space-between;
          overflow: hidden;
        }

        .v-combobox__selection + .v-combobox__selection,
        .v-select__selection + .v-select__selection {
          margin-left: 4px;
        }

        .v-select__selections {
          display: flex;
          flex-wrap: nowrap;
          justify-content: flex-end;
          overflow: hidden;
          
          input {
            min-width: 30px;
          }
        }
      }
    }
  }
</style>
